import React, { useState, useEffect } from 'react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

/**
 * WtfSyncBadge - Shows sync freshness status for WTF data feeds.
 * Displays a colored dot (green/yellow/red) with optional label text.
 * Used in table headers and cards to indicate WTF data source health.
 *
 * @param {Date|null} lastSync - When feed was last synced
 * @param {boolean} stale - Whether backend reports staleness
 * @param {string} size - Badge size: "sm" or "md" (default "sm")
 * @param {boolean} showLabel - Show "WTF Live" text or just the dot (default true)
 */
export function WtfSyncBadge({ lastSync, stale, size = "sm", showLabel = true }) {
  const [secondsAgo, setSecondsAgo] = useState(null);

  // Update secondsAgo every 5 seconds for live freshness display
  useEffect(() => {
    const updateAge = () => {
      if (lastSync) {
        setSecondsAgo(Math.floor((Date.now() - lastSync.getTime()) / 1000));
      } else {
        setSecondsAgo(null);
      }
    };

    updateAge();
    const timer = setInterval(updateAge, 5000);
    return () => clearInterval(timer);
  }, [lastSync]);

  // Color and label logic
  let dotColor, label, dotClasses;

  if (secondsAgo === null || secondsAgo > 120 || stale) {
    dotColor = 'bg-red-500';
    dotClasses = dotColor;
    label = 'Stale';
  } else if (secondsAgo > 60) {
    dotColor = 'bg-yellow-500';
    dotClasses = dotColor;
    label = `Synced ${secondsAgo}s ago`;
  } else {
    dotColor = 'bg-green-500';
    dotClasses = `${dotColor} animate-pulse`;
    label = 'Live';
  }

  const dotSize = size === "md" ? "h-2.5 w-2.5" : "h-2 w-2";
  const textSize = size === "md" ? "text-xs" : "text-[10px]";

  const tooltipText = lastSync
    ? `Last synced: ${lastSync.toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata' })}`
    : 'Not yet synced';

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className={`inline-flex items-center gap-1.5 cursor-default ${textSize}`}>
            <span className={`${dotSize} rounded-full ${dotClasses} inline-block flex-shrink-0`} />
            {showLabel && (
              <span className="text-gray-500 whitespace-nowrap">
                {label}
              </span>
            )}
          </span>
        </TooltipTrigger>
        <TooltipContent>
          <p>{tooltipText}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
